import { useEffect, useState } from 'react';
import axios from 'axios';

export interface NursingStaffActivityRow {
  researcherId: number;
  name: string;
  surnames: string;
  role: string | null;
  service: string | null;
  counts: Record<number, number>;
  administrations: number;
}

export interface NursingStaffActivityData {
  hasNursingActivity: boolean;
  surveys: { id: number; name: string }[];
  rows: NursingStaffActivityRow[];
  administrationTotal: number;
  unattributed: {
    NONE: number;
    NOT_APPLICABLE: number;
    NULL: number;
  };
}

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; data: NursingStaffActivityData }
  | { status: 'error'; error: Error };

/**
 * SHER-1001. A dedicated fetch, not another chart endpoint: the chart lists are
 * asserted exactly by the view-mode tests, and this table is not a chart.
 */
export function useNursingStaffActivity(params: {
  investigationUuid: string | undefined;
  departmentSelected: string;
  startDate: number | null;
  endDate: number | null;
  context: 'inpatient' | 'outpatient';
  enabled: boolean;
}): LoadState {
  const { investigationUuid, departmentSelected, startDate, endDate, context, enabled } = params;
  const [state, setState] = useState<LoadState>({ status: 'idle' });

  useEffect(() => {
    if (!enabled || !investigationUuid || startDate == null || endDate == null) {
      setState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    const department = departmentSelected || 'all';
    const segment = context === 'inpatient' ? 'inpatients' : 'outpatients';
    const url = `${import.meta.env.VITE_APP_API_URL}/analytics/${investigationUuid}/${segment}/nursing-staff-activity/${department}/startDate/${startDate}/endDate/${endDate}`;
    axios.get(url, { headers: { Authorization: localStorage.getItem('jwt') } })
      .then((response) => {
        if (!cancelled) {
          setState({ status: 'ok', data: response.data });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: 'error', error: error instanceof Error ? error : new Error(String(error)) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, investigationUuid, departmentSelected, startDate, endDate, context]);

  return state;
}
